import { defineStore } from 'pinia'
import { ref } from 'vue'

export type ToastSeverity = 'success' | 'info' | 'warn' | 'error'

export interface ToastMessage {
  id: string
  severity: ToastSeverity
  summary: string
  detail?: string
  life?: number
}

export const useNotificationsStore = defineStore('notifications', () => {
  const queue = ref<ToastMessage[]>([])
  const seen = new Set<string>() // dedup key → expires after life ms

  function push(msg: Omit<ToastMessage, 'id'>) {
    const dedupKey = `${msg.severity}:${msg.summary}`
    if (seen.has(dedupKey)) return
    seen.add(dedupKey)
    const life = msg.life ?? 4000
    setTimeout(() => seen.delete(dedupKey), life + 500)

    const toast: ToastMessage = { ...msg, id: crypto.randomUUID(), life }
    queue.value.push(toast)
  }

  function dismiss(id: string) {
    queue.value = queue.value.filter((t) => t.id !== id)
  }

  function success(summary: string, detail?: string) {
    push({ severity: 'success', summary, detail })
  }

  function error(summary: string, detail?: string) {
    push({ severity: 'error', summary, detail, life: 6000 })
  }

  function warn(summary: string, detail?: string) {
    push({ severity: 'warn', summary, detail })
  }

  function info(summary: string, detail?: string) {
    push({ severity: 'info', summary, detail })
  }

  return { queue, push, dismiss, success, error, warn, info }
})
